import {
  type CalendarCategoryFilter,
  type CalendarFilterCategory,
  type CalendarFilterMode,
  type CalendarFilterOption,
  type CalendarModeScope,
} from './types'

/**
 * How one category's values are organised: which of them sit under which — an
 * account under its channel, a label under its set — and whose choice Include
 * or Exclude is.
 *
 * The shape is built from the option list alone, because an option names the
 * heading it belongs to. The mode scope is the one thing the options cannot
 * say, so it travels with them: everything that reads or writes a category's
 * two lists is already handed its tree, and would otherwise read a set matched
 * beside a set hidden as a filter written down wrong.
 */
export type CalendarValueTree = Readonly<{
  childrenOf: ReadonlyMap<string, readonly string[]>
  modeScope: CalendarModeScope
  parentOf: ReadonlyMap<string, string>
}>

/** One tree per category, by category id. */
export type CalendarValueTrees = Readonly<Record<string, CalendarValueTree>>

/**
 * The tree behind one option list.
 *
 * A heading that is an option of its own names itself as its group — the
 * channel convention — and is nobody's child. Every other option offered under
 * such a heading is a child of it.
 *
 * A group nobody offers as a row heads nothing. It only labels the rows under
 * it, and a row a person cannot choose must never stand for the rows it
 * labels: the filter would then be written down as a value that is offered
 * nowhere.
 */
export const buildValueTree = (
  options: readonly CalendarFilterOption[],
  modeScope: CalendarModeScope = 'category'
): CalendarValueTree => {
  const childrenOf = new Map<string, readonly string[]>()
  const parentOf = new Map<string, string>()
  const offered = new Set(options.map((option) => option.id))

  options.forEach(({ group, id }) => {
    if (group === undefined || group.id === id || !offered.has(group.id)) return

    parentOf.set(id, group.id)
    childrenOf.set(group.id, [...(childrenOf.get(group.id) ?? []), id])
  })

  return { childrenOf, modeScope, parentOf }
}

/** The tree of every category, ready for the matcher. */
export const buildValueTrees = (
  categories: readonly CalendarFilterCategory[]
): CalendarValueTrees =>
  Object.fromEntries(
    categories.map((category) => [
      category.id,
      buildValueTree(category.options, category.modeScope),
    ])
  )

/**
 * The chosen values, and everything under them.
 *
 * This is what makes choosing a parent mean anything: an item carries its own
 * labels, never the set they belong to, so a set is only a filter once it
 * stands for its labels. A category with no tree hands back what it was given.
 */
export const expandValues = (
  values: readonly string[],
  tree: CalendarValueTree | undefined
): ReadonlySet<string> =>
  tree === undefined
    ? new Set(values)
    : new Set(
        values.flatMap((value) => [
          value,
          ...(tree.childrenOf.get(value) ?? []),
        ])
      )

/** Include first: it is what a filter does unless a person says otherwise. */
export const FILTER_MODES: readonly CalendarFilterMode[] = [
  'include',
  'exclude',
]

export const otherFilterMode = (
  mode: CalendarFilterMode
): CalendarFilterMode => (mode === 'include' ? 'exclude' : 'include')

/**
 * The mode a category's two lists say where nothing names it. A category with
 * values to hide and nothing to match hides them; any other category matches.
 * Only a hidden row that keeps a carve-out back holds values in both lists, and
 * that shape has to name its mode.
 */
export const impliedFilterMode = ({
  exclude,
  include,
}: Pick<CalendarCategoryFilter, 'exclude' | 'include'>): CalendarFilterMode =>
  include.length === 0 && exclude.length > 0 ? 'exclude' : 'include'

/** One mode's worth of a category: what it holds, and the exceptions to it. */
export type CalendarFilterGroup = Readonly<{
  carved: readonly string[]
  chosen: readonly string[]
  mode: CalendarFilterMode
}>

/**
 * One category: the values it filters by, and the carve-outs under them.
 *
 * `chosen` is the list the mode names — what the category matches, or what it
 * hides. `carved` is the other list, and it holds nothing but exceptions to a
 * row in `chosen`: "this channel except that account", either way round.
 */
export const categoryFilterOf = (
  mode: CalendarFilterMode,
  chosen: readonly string[],
  carved: readonly string[] = []
): CalendarCategoryFilter =>
  mode === 'include'
    ? { exclude: carved, include: chosen, mode }
    : { exclude: chosen, include: carved, mode }

/** The row a value belongs under, or the value itself where it heads one. */
export const groupOf = (valueId: string, tree: CalendarValueTree): string =>
  tree.parentOf.get(valueId) ?? valueId

/** Whether a row holds anything at all, in either list. */
const hasTicks = (
  parentId: string,
  { exclude, include }: CalendarCategoryFilter,
  tree: CalendarValueTree
): boolean =>
  [parentId, ...(tree.childrenOf.get(parentId) ?? [])].some(
    (valueId) => include.includes(valueId) || exclude.includes(valueId)
  )

/**
 * Which way round the row a value sits under is read, where the mode belongs to
 * each row rather than to the category. Pass the row itself or anything under
 * it; both answer for the row.
 *
 * A row that holds something says so itself: it is hidden because its own ticks
 * are in the exclude list, and the labels the include list keeps back from it
 * are its carve-outs rather than a second opinion. A row that holds nothing has
 * nowhere to say it, so `parentModes` answers for it until its first tick
 * lands. Include is what a filter does unless a person says otherwise.
 */
export const parentFilterMode = (
  valueId: string,
  filter: CalendarCategoryFilter,
  tree: CalendarValueTree
): CalendarFilterMode => {
  const { exclude, include, parentModes } = filter
  const parentId = groupOf(valueId, tree)
  if (include.includes(parentId)) return 'include'
  if (exclude.includes(parentId)) return 'exclude'

  const children = tree.childrenOf.get(parentId) ?? []
  if (children.some((child) => include.includes(child))) return 'include'
  if (children.some((child) => exclude.includes(child))) return 'exclude'

  return parentModes?.[parentId] ?? 'include'
}

/**
 * One list and its exceptions, written the shortest way that means the same.
 *
 * A parent already chosen makes its children redundant, and a list holding
 * every child of a parent is that parent.
 *
 * The other list is exceptions and nothing else. A value there with no chosen
 * row above it is a filter no box in the drawer could draw, so it is dropped —
 * which is how an address written by hand, or under an older rule, is read. A
 * row every one of whose children is carved out matches nothing either way, so
 * the row goes with them.
 */
const normalizeLists = (
  chosenValues: readonly string[],
  carvedValues: readonly string[],
  tree: CalendarValueTree
): Readonly<{ carved: string[]; chosen: string[] }> => {
  const drop = (values: readonly string[], unwanted: readonly string[]) =>
    values.filter((value) => !unwanted.includes(value))

  let chosen = [...chosenValues]
  tree.childrenOf.forEach((children, parentId) => {
    if (chosen.includes(parentId)) chosen = drop(chosen, children)
    else if (children.every((child) => chosen.includes(child)))
      chosen = [...drop(chosen, children), parentId]
  })

  let carved = carvedValues.filter((valueId) => {
    const parentId = tree.parentOf.get(valueId)
    return (
      parentId !== undefined &&
      chosen.includes(parentId) &&
      !chosen.includes(valueId)
    )
  })

  tree.childrenOf.forEach((children, parentId) => {
    if (!chosen.includes(parentId)) return
    if (children.every((child) => carved.includes(child))) {
      chosen = drop(chosen, [parentId])
      carved = drop(carved, children)
    }
  })

  return { carved, chosen }
}

/**
 * The same choice for a category whose parents each answer for themselves.
 *
 * Every parent is tidied on its own, so one set can sit in the include list
 * while the next sits in the exclude list, and the carve-outs of each stay
 * beside the set they are exceptions to. The category itself says nothing, so
 * it is handed back without a mode.
 */
const normalizeByParent = (
  category: CalendarCategoryFilter,
  tree: CalendarValueTree
): CalendarCategoryFilter => {
  const lists: Record<CalendarFilterMode, string[]> = {
    exclude: [],
    include: [],
  }
  const parentIds = new Set(
    [...category.include, ...category.exclude].map((valueId) =>
      groupOf(valueId, tree)
    )
  )

  parentIds.forEach((parentId) => {
    const mine = (values: readonly string[]) =>
      values.filter((value) => groupOf(value, tree) === parentId)

    const mode = parentFilterMode(parentId, category, tree)
    const other = otherFilterMode(mode)
    const { carved, chosen } = normalizeLists(
      mine(category[mode]),
      mine(category[other]),
      tree
    )

    lists[mode].push(...chosen)
    lists[other].push(...carved)
  })

  // A row that now holds something says its own mode, so what was remembered
  // for it is not just redundant but a second answer waiting to disagree.
  // Include is remembered nowhere: it is what a row does anyway.
  const next = { exclude: lists.exclude, include: lists.include }
  const remembered = Object.entries(category.parentModes ?? {}).filter(
    ([parentId, mode]) => mode === 'exclude' && !hasTicks(parentId, next, tree)
  )

  return remembered.length === 0
    ? next
    : { ...next, parentModes: Object.fromEntries(remembered) }
}

/**
 * The same choice written in the shortest way that means it, and in the only
 * shape a category is allowed to hold.
 */
export const normalizeCategoryFilter = (
  category: CalendarCategoryFilter,
  tree: CalendarValueTree
): CalendarCategoryFilter => {
  if (tree.modeScope === 'parent') return normalizeByParent(category, tree)

  const mode = category.mode ?? 'include'
  const { carved, chosen } = normalizeLists(
    category[mode],
    category[otherFilterMode(mode)],
    tree
  )

  return categoryFilterOf(mode, chosen, carved)
}

/**
 * What is chosen in one category, split into the groups a person reads it in:
 * what it matches, and what it hides.
 *
 * A category with one mode has one group however much it holds. A category
 * whose parents each answer for themselves has two whenever a person has
 * matched one set and hidden another, and each group carries its own
 * carve-outs. Either can come back empty; the drawer draws no heading over
 * tokens it does not have.
 */
export const categoryFilterGroups = (
  category: CalendarCategoryFilter,
  tree: CalendarValueTree
): CalendarFilterGroup[] => {
  if (tree.modeScope !== 'parent') {
    const mode = category.mode ?? 'include'
    return [
      { carved: category[otherFilterMode(mode)], chosen: category[mode], mode },
    ]
  }

  const isIn = (mode: CalendarFilterMode) => (valueId: string) =>
    parentFilterMode(valueId, category, tree) === mode

  return FILTER_MODES.map((mode) => ({
    carved: category[otherFilterMode(mode)].filter(isIn(mode)),
    chosen: category[mode].filter(isIn(mode)),
    mode,
  }))
}

/** Which of the two lists holds a value outright, if either does. */
const listOf = (
  valueId: string,
  include: readonly string[],
  exclude: readonly string[]
): CalendarFilterMode | undefined =>
  include.includes(valueId)
    ? 'include'
    : exclude.includes(valueId)
      ? 'exclude'
      : undefined

/**
 * How many values under one row are in the filter, counting both lists.
 *
 * A row chosen whole stands for everything under it, less whatever the other
 * list carves back out, so the number says what is filtered rather than how
 * many times somebody clicked.
 */
export const chosenUnderParent = (
  parentId: string,
  include: readonly string[],
  exclude: readonly string[],
  tree: CalendarValueTree
): number => {
  const children = tree.childrenOf.get(parentId) ?? []
  const holder = listOf(parentId, include, exclude)

  if (holder !== undefined) {
    const other = holder === 'include' ? exclude : include
    return children.filter((child) => !other.includes(child)).length
  }

  return children.filter(
    (child) => include.includes(child) || exclude.includes(child)
  ).length
}

/**
 * Which list a value reads as being in, whichever tab a person has open.
 *
 * A row says what the filter holds, not what the next click would write, so
 * both lists are read here and neither is privileged. A value in a list is in
 * it. A value under a chosen row is in that row's list with it — which is what
 * lets a channel stand for its accounts — until it is written into the opposite
 * list, and then it is the carve-out: "all of this channel except this one". A
 * row that stands over values in one list wears that list's mark, so a channel
 * with two excluded accounts is not painted as something being matched.
 *
 * `undefined` means the value is in neither list.
 */
export const valueList = (
  valueId: string,
  include: readonly string[],
  exclude: readonly string[],
  tree: CalendarValueTree
): CalendarFilterMode | undefined => {
  const own = listOf(valueId, include, exclude)
  if (own !== undefined) return own

  const parentId = tree.parentOf.get(valueId)
  const inherited =
    parentId === undefined ? undefined : listOf(parentId, include, exclude)
  if (inherited !== undefined) return inherited

  const children = tree.childrenOf.get(valueId) ?? []
  if (children.some((child) => include.includes(child))) return 'include'
  if (children.some((child) => exclude.includes(child))) return 'exclude'
  return undefined
}
